import { useEffect, useRef, useState } from "react";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import {
  ArrowLeft, ArrowRight, ArrowUpRight, Clapperboard, MessagesSquare, Search, TrendingUp, Users,
  UsersRound,
} from "lucide-react";
import { Link } from "react-router-dom";
import { Eyebrow } from "@/components/brand/Bits";
import { FUNNEL, SERVICES } from "@/data/siteContent";

const ICONS = { Users, Clapperboard, MessagesSquare, TrendingUp, Search, UsersRound };

function ServiceCard({ service, index, total }) {
  const Icon = ICONS[service.icon] || Users;
  return (
    <article
      className="group relative flex h-full w-[82vw] shrink-0 snap-center flex-col justify-between overflow-hidden rounded-3xl border border-[#E6E1D8] bg-white p-7 sm:w-[60vw] lg:w-[420px] lg:p-9"
      data-testid={`service-card-${service.slug}`}
    >
      <span
        aria-hidden
        className="pointer-events-none absolute -right-16 -top-16 h-52 w-52 rounded-full bg-[#F2811D]/10 blur-3xl transition-colors duration-500 group-hover:bg-[#F2811D]/20"
      />
      <div className="relative">
        <div className="flex items-start justify-between gap-4">
          <span className="font-heading text-5xl font-extrabold leading-none text-[#EDE6DA] lg:text-6xl">
            {String(index + 1).padStart(2, "0")}
          </span>
          <span className="rounded-full bg-[#111111] p-3 text-white">
            <Icon className="h-5 w-5" aria-hidden />
          </span>
        </div>
        <p className="mt-6 text-[11px] font-bold uppercase tracking-[0.14em] text-[#F2811D]">
          {service.stage}
        </p>
        <h3 className="mt-3 font-heading text-2xl font-extrabold leading-tight tracking-tight lg:text-3xl">
          {service.name}
        </h3>
        <p className="mt-3.5 text-[15px] leading-relaxed body-text lg:text-base">
          {service.description}
        </p>
      </div>
      <div className="relative mt-8 flex items-center justify-between gap-3">
        <Link
          to="/services"
          className="inline-flex items-center gap-1.5 text-sm font-bold text-[#111111] transition-colors hover:text-[#F2811D]"
          data-testid={`service-card-link-${service.slug}`}
        >
          How it works
          <ArrowUpRight className="h-4 w-4" />
        </Link>
        <span className="text-xs font-bold tabular text-[#B5AEA2]">
          {index + 1}/{total}
        </span>
      </div>
    </article>
  );
}

/** Desktop: cards travel horizontally while the section is pinned. Mobile: swipeable track. */
export function ServiceShowcase() {
  const wrapper = useRef(null);
  const track = useRef(null);
  const [wide, setWide] = useState(false);
  const still = useReducedMotion();

  useEffect(() => {
    const query = window.matchMedia("(min-width: 1024px)");
    const apply = () => setWide(query.matches);
    apply();
    query.addEventListener("change", apply);
    return () => query.removeEventListener("change", apply);
  }, []);

  const pinned = wide && !still;
  const { scrollYProgress } = useScroll({
    target: wrapper,
    offset: ["start start", "end end"],
    layoutEffect: false,
  });
  const distance = Math.max(0, SERVICES.length - 2) * 446;
  const x = useTransform(scrollYProgress, [0, 1], [0, -distance]);

  const step = (direction) => {
    if (pinned && wrapper.current) {
      const box = wrapper.current.getBoundingClientRect();
      const perCard = (box.height - window.innerHeight) / Math.max(1, SERVICES.length - 1);
      window.scrollBy({ top: direction * perCard, behavior: still ? "auto" : "smooth" });
      return;
    }
    track.current?.scrollBy({ left: direction * 360, behavior: still ? "auto" : "smooth" });
  };

  const header = (
    <div className="max-w-3xl">
      <Eyebrow>{FUNNEL.eyebrow}</Eyebrow>
      <h2 className="mt-5 font-heading text-3xl font-extrabold leading-tight tracking-tight sm:text-4xl">
        {FUNNEL.title}
      </h2>
      <p className="mt-5 text-base leading-relaxed body-text md:text-lg">{FUNNEL.body}</p>
    </div>
  );

  const controls = (
    <div className="flex items-center gap-2">
      <button
        type="button"
        onClick={() => step(-1)}
        aria-label="Previous service"
        data-testid="showcase-prev"
        className="rounded-full border border-[#E4DED3] bg-white p-2.5 transition-colors hover:border-[#111111]"
      >
        <ArrowLeft className="h-4 w-4" />
      </button>
      <button
        type="button"
        onClick={() => step(1)}
        aria-label="Next service"
        data-testid="showcase-next"
        className="rounded-full border border-[#E4DED3] bg-white p-2.5 transition-colors hover:border-[#111111]"
      >
        <ArrowRight className="h-4 w-4" />
      </button>
    </div>
  );

  const footer = (
    <div className="mt-8 flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-[#E6E1D8] bg-white p-6">
      <p className="max-w-2xl text-[15px] font-bold leading-relaxed">{FUNNEL.logic}</p>
      <div className="flex flex-wrap gap-2.5">
        <Link
          to="/services"
          className="inline-flex items-center gap-1.5 rounded-full border border-[#111111] px-5 py-2.5 text-sm font-bold transition-colors hover:bg-[#111111] hover:text-white"
          data-testid="showcase-services-link"
        >
          See all services
        </Link>
        <Link
          to="/contact"
          className="inline-flex items-center gap-1.5 rounded-full bg-[#F2811D] px-5 py-2.5 text-sm font-bold text-white transition-colors hover:bg-[#d8700f]"
          data-testid="showcase-enquiry-link"
        >
          Start an enquiry
          <ArrowUpRight className="h-4 w-4" />
        </Link>
      </div>
    </div>
  );

  const cards = SERVICES.map((service, index) => (
    <ServiceCard key={service.slug} service={service} index={index} total={SERVICES.length} />
  ));

  if (!pinned) {
    return (
      <section ref={wrapper} className="bg-[#F7F4EF] py-20 md:py-24" data-testid="funnel-section">
        <div className="mx-auto max-w-[1180px] px-5">
          <div className="flex flex-wrap items-end justify-between gap-5">
            {header}
            {controls}
          </div>
          <div
            ref={track}
            className="scroll-x mt-10 flex snap-x snap-mandatory gap-5 pb-4"
            data-testid="showcase-track"
          >
            {cards}
          </div>
          <p
            className="text-xs font-bold uppercase tracking-[0.14em] text-[#8a8378]"
            data-testid="showcase-swipe-cue"
          >
            Swipe for more services →
          </p>
          {footer}
        </div>
      </section>
    );
  }

  return (
    <section
      ref={wrapper}
      className="relative bg-[#F7F4EF]"
      style={{ height: `${SERVICES.length * 62}vh` }}
      data-testid="funnel-section"
    >
      <div className="sticky top-0 flex h-screen flex-col justify-center overflow-hidden py-14">
        <div className="mx-auto w-full max-w-[1180px] px-5">
          <div className="flex flex-wrap items-end justify-between gap-5">
            {header}
            {controls}
          </div>
        </div>
        <div className="mt-9 overflow-hidden">
          <motion.div
            style={{ x }}
            className="flex gap-6 pl-[max(1.25rem,calc((100vw-1180px)/2))]"
            data-testid="showcase-track"
          >
            {cards}
          </motion.div>
        </div>
        <div className="mx-auto mt-7 w-full max-w-[1180px] px-5">
          <div className="h-1 w-full overflow-hidden rounded-full bg-[#E4DED3]">
            <motion.div
              className="h-full bg-[#F2811D]"
              style={{ scaleX: scrollYProgress, transformOrigin: "left" }}
              data-testid="showcase-progress"
            />
          </div>
          <p className="mt-4 max-w-2xl text-sm font-bold leading-relaxed">{FUNNEL.logic}</p>
          <div className="mt-4 flex flex-wrap gap-2.5">
            <Link
              to="/services"
              className="inline-flex items-center gap-1.5 rounded-full border border-[#111111] px-5 py-2.5 text-sm font-bold transition-colors hover:bg-[#111111] hover:text-white"
              data-testid="showcase-services-link"
            >
              See all services
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center gap-1.5 rounded-full bg-[#F2811D] px-5 py-2.5 text-sm font-bold text-white transition-colors hover:bg-[#d8700f]"
              data-testid="showcase-enquiry-link"
            >
              Start an enquiry
              <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
